/**
 * The dev-only Module Editor toggle, and whether the session asked for it.
 *
 * @module components/editor/DevModeToggle
 */

import React, { useCallback, useEffect } from "react";

import { useLocation } from "react-router";

import { Toggle } from "@/components/ui";
import { useEditSession } from "@/platform/editor-sync-context";
import { focusStepForMode, withMode } from "@/state/store";

/** Where the dev-UI flag lives once `?dev=true` has named it. */
const DEV_UI_KEY = "socratiq_dev_ui";

/**
 * Whether the dev-only controls show. Read from the address when it says so,
 * and remembered for the tab afterwards, because every in-app navigation
 * drops the query the flag arrived on.
 */
function rememberedDevMode(): boolean {
  try {
    return window.sessionStorage.getItem(DEV_UI_KEY) === "true";
  } catch {
    // Private windows and blocked site data throw on access.
    return false;
  }
}

/** Writes the flag the address asked for. Idempotent, so an effect may run it twice. */
function rememberDevMode(asked: string | null): void {
  try {
    if (asked === "true") {
      window.sessionStorage.setItem(DEV_UI_KEY, "true");
    } else if (asked === "false") {
      window.sessionStorage.removeItem(DEV_UI_KEY);
    }
  } catch {
    // Nothing to remember it in; the address still decides this render.
  }
}

/** Whether the dev-only controls show for this tab. */
export function useDevMode(): boolean {
  // `?dev=true` names the session, not one address: the first client-side
  // navigation drops the query, so a flag read off the current URL left the
  // toggle unreachable the moment the reader opened any other section.
  // `?dev=false` puts it away again.
  const { search } = useLocation();
  const asked = new URLSearchParams(search).get("dev");
  // Read every render, not once: the bar is rendered by the layout route, so
  // moving between sections never remounts it, and a flag captured at mount
  // is the value from whichever address the reader arrived on.
  const isDevMode = asked === "true" || (asked !== "false" && rememberedDevMode());
  useEffect(() => {
    rememberDevMode(asked);
  }, [asked]);
  return isDevMode;
}

/** Switches the programme between owner and module-editor mode. */
export function DevModeToggle({ mode }: { mode: string | undefined }): React.ReactElement {
  const { edit } = useEditSession();
  const handleDevModeToggle = useCallback(
    (checked: boolean) => {
      const next = checked ? "MODULE_EDITOR" : "PROGRAMME_OWNER";
      edit(withMode(next), { save: "now" });
      focusStepForMode(next);
    },
    [edit],
  );

  return (
    <Toggle
      size="sm"
      id="devModeToggle"
      label="Module Editor"
      checked={mode === "MODULE_EDITOR"}
      onChange={handleDevModeToggle}
      data-testid="dev-mode-toggle"
    />
  );
}
